import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const HeroSettings = () => {
  const [form, setForm] = useState({
    headingLine1: '',
    headingHighlight: '',
    headingLine2: '',
    descriptionText: '',
    primaryButtonLabel: '',
    primaryButtonPath: '',
    secondaryButtonLabel: '',
    secondaryButtonUrl: '',
    heroBackgroundUrl: '',
    heroMainImageUrl: '',
    heroAvatarName: '',
    heroAvatarTitle: '',
    stat1Label: '',
    stat1Percent: '',
    stat2Label: '',
    stat2Percent: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setLoading(false);
      return;
    }

    const fetchHero = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/admin/hero`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { hero } = res.data;
        if (hero) {
          setForm({
            headingLine1: hero.headingLine1 || '',
            headingHighlight: hero.headingHighlight || '',
            headingLine2: hero.headingLine2 || '',
            descriptionText: hero.descriptionText || '',
            primaryButtonLabel: hero.primaryButtonLabel || '',
            primaryButtonPath: hero.primaryButtonPath || '',
            secondaryButtonLabel: hero.secondaryButtonLabel || '',
            secondaryButtonUrl: hero.secondaryButtonUrl || '',
            heroBackgroundUrl: hero.heroBackgroundUrl || '',
            heroMainImageUrl: hero.heroMainImageUrl || '',
            heroAvatarName: hero.heroAvatarName || '',
            heroAvatarTitle: hero.heroAvatarTitle || '',
            stat1Label: hero.stat1Label || '',
            stat1Percent:
              typeof hero.stat1Percent === 'number' ? String(hero.stat1Percent) : '',
            stat2Label: hero.stat2Label || '',
            stat2Percent:
              typeof hero.stat2Percent === 'number' ? String(hero.stat2Percent) : '',
          });
        }
      } catch {
        // ignore, will use defaults
      } finally {
        setLoading(false);
      }
    };

    fetchHero();
  }, []);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const uploadImage = async (file, onUrl) => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setError('انتهت صلاحية الجلسة، برجاء تسجيل الدخول مرة أخرى.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post(`${API_BASE_URL}/api/admin/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      if (res.data?.url) {
        onUrl(res.data.url);
        setSuccess('تم رفع الصورة بنجاح.');
      }
    } catch (err) {
      const message = err.response?.data?.message || 'فشل رفع الصورة. تأكد من أن الملف صورة أقل من 5MB.';
      setError(message);
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);

    const token = localStorage.getItem('adminToken');
    if (!token) {
      setError('انتهت صلاحية الجلسة، برجاء تسجيل الدخول مرة أخرى.');
      setSaving(false);
      return;
    }

    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/hero`,
        {
          ...form,
          stat1Percent: form.stat1Percent ? Number(form.stat1Percent) : null,
          stat2Percent: form.stat2Percent ? Number(form.stat2Percent) : null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSuccess('تم حفظ إعدادات قسم الهيرو بنجاح.');
    } catch (err) {
      const message = err.response?.data?.message || 'حدث خطأ أثناء الحفظ.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p>جاري تحميل إعدادات قسم الهيرو...</p>;
  }

  return (
    <div className="admin-section">
      <h2 className="admin-section-title">إعدادات قسم الهيرو (الجزء الرئيسي في الصفحة)</h2>
      <p className="admin-section-subtitle">
        يمكنك هنا تعديل العنوان الرئيسي، النص الوصفي، أزرار الدعوة لاتخاذ إجراء، وصورة البانر.
      </p>

      {error && <div className="admin-section-error">{error}</div>}
      {success && <div className="admin-section-success">{success}</div>}

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>العنوان - السطر الأول</label>
          <input
            type="text"
            value={form.headingLine1}
            onChange={(e) => updateField('headingLine1', e.target.value)}
            placeholder="نبض كير، شريكك الاستراتيجي"
          />
        </div>
        <div className="admin-field">
          <label>العنوان - النص المميز (سطر في المنتصف)</label>
          <input
            type="text"
            value={form.headingHighlight}
            onChange={(e) => updateField('headingHighlight', e.target.value)}
            placeholder="للعبور بمنتجاتك"
          />
        </div>
      </div>

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>العنوان - السطر الأخير</label>
          <input
            type="text"
            value={form.headingLine2}
            onChange={(e) => updateField('headingLine2', e.target.value)}
            placeholder="إلى الأسواق السعودية"
          />
        </div>
        <div className="admin-field">
          <label>النص الوصفي</label>
          <input
            type="text"
            value={form.descriptionText}
            onChange={(e) => updateField('descriptionText', e.target.value)}
            placeholder="في نبض كير نقدّم حلولًا استشارية متكاملة..."
          />
        </div>
      </div>

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>زر رئيسي - النص</label>
          <input
            type="text"
            value={form.primaryButtonLabel}
            onChange={(e) => updateField('primaryButtonLabel', e.target.value)}
            placeholder="خدماتنا"
          />
        </div>
        <div className="admin-field">
          <label>زر رئيسي - المسار</label>
          <input
            type="text"
            value={form.primaryButtonPath}
            onChange={(e) => updateField('primaryButtonPath', e.target.value)}
            placeholder="/services"
          />
        </div>
      </div>

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>زر ثانوي - النص</label>
          <input
            type="text"
            value={form.secondaryButtonLabel}
            onChange={(e) => updateField('secondaryButtonLabel', e.target.value)}
            placeholder="اتصل بنا"
          />
        </div>
        <div className="admin-field">
          <label>زر ثانوي - رابط (مثلاً واتساب)</label>
          <input
            type="text"
            value={form.secondaryButtonUrl}
            onChange={(e) => updateField('secondaryButtonUrl', e.target.value)}
            placeholder="https://wa.me/+9665xxxxxxx"
          />
        </div>
      </div>

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>رابط صورة الخلفية (اختياري)</label>
          <input
            type="text"
            value={form.heroBackgroundUrl}
            onChange={(e) => updateField('heroBackgroundUrl', e.target.value)}
            placeholder="/uploads/hero-bg.webp أو رابط خارجي"
          />
          <small>يمكنك رفع صورة الخلفية أو استخدام رابط خارجي.</small>
          <div style={{ marginTop: '0.5rem' }}>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  uploadImage(file, (url) => updateField('heroBackgroundUrl', url));
                }
              }}
            />
          </div>
        </div>
        <div className="admin-field">
          <label>رابط صورة الشخص (اختياري)</label>
          <input
            type="text"
            value={form.heroMainImageUrl}
            onChange={(e) => updateField('heroMainImageUrl', e.target.value)}
            placeholder="/uploads/hero-main.webp أو رابط خارجي"
          />
          <small>يمكنك رفع صورة الشخص أو استخدام رابط خارجي.</small>
          <div style={{ marginTop: '0.5rem' }}>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  uploadImage(file, (url) => updateField('heroMainImageUrl', url));
                }
              }}
            />
          </div>
        </div>
      </div>

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>اسم الشخص في الكرت العائم</label>
          <input
            type="text"
            value={form.heroAvatarName}
            onChange={(e) => updateField('heroAvatarName', e.target.value)}
            placeholder="محمد العلي"
          />
        </div>
        <div className="admin-field">
          <label>المسمى الوظيفي في الكرت العائم</label>
          <input
            type="text"
            value={form.heroAvatarTitle}
            onChange={(e) => updateField('heroAvatarTitle', e.target.value)}
            placeholder="مدير الاستشارات"
          />
        </div>
      </div>

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>إحصائية 1 - النص</label>
          <input
            type="text"
            value={form.stat1Label}
            onChange={(e) => updateField('stat1Label', e.target.value)}
            placeholder="رضا العملاء (99%)"
          />
        </div>
        <div className="admin-field">
          <label>إحصائية 1 - النسبة %</label>
          <input
            type="number"
            min="0"
            max="100"
            value={form.stat1Percent}
            onChange={(e) => updateField('stat1Percent', e.target.value)}
            placeholder="99"
          />
        </div>
      </div>

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>إحصائية 2 - النص</label>
          <input
            type="text"
            value={form.stat2Label}
            onChange={(e) => updateField('stat2Label', e.target.value)}
            placeholder="تخليص جمركي (95%)"
          />
        </div>
        <div className="admin-field">
          <label>إحصائية 2 - النسبة %</label>
          <input
            type="number"
            min="0"
            max="100"
            value={form.stat2Percent}
            onChange={(e) => updateField('stat2Percent', e.target.value)}
            placeholder="95"
          />
        </div>
      </div>

      <div className="admin-actions">
        <button
          type="button"
          className="admin-btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
        </button>
      </div>
    </div>
  );
};

export default HeroSettings;

